import { useId, useState } from 'react';
import { CalendarRange } from 'lucide-react';
import { usePaneQuery } from '../lib/filter-context';
import type { PanePeriodToken } from '../../shared/contracts';

const PERIODS: { value: PanePeriodToken; label: string }[] = [
  { value: 'thisMonth', label: 'Denne måned' },
  { value: 'lastMonth', label: 'Sidste måned' },
  { value: 'nextMonth', label: 'Næste måned' },
  { value: 'thisQuarter', label: 'Dette kvartal' },
  { value: 'lastQuarter', label: 'Sidste kvartal' },
  { value: 'thisYear', label: 'I år' },
  { value: 'lastYear', label: 'Sidste år' },
];

type Mode = 'default' | PanePeriodToken | 'custom';

function deriveMode(period: ReturnType<typeof usePaneQuery>['query']['period']): Mode {
  if (period == null) return 'default';
  return typeof period === 'string' ? period : 'custom';
}

const SELECT_CLASS =
  'rounded-brand border border-brand-primary/15 bg-white px-2.5 py-1.5 text-sm text-brand-dark focus:border-brand-primary/40 focus:outline-none';

/**
 * Period / date-browsing control. Drives ctx.query.period for every pane on the page;
 * panes that don't support a period (snapshot lists, agenda, trailing trends) ignore it.
 */
export function PeriodFilter() {
  const { query, setQuery } = usePaneQuery();
  const selectId = useId();
  const [mode, setMode] = useState<Mode>(deriveMode(query.period));
  const initialCustom = typeof query.period === 'object' ? query.period : null;
  const [from, setFrom] = useState(initialCustom?.from ?? '');
  const [to, setTo] = useState(initialCustom?.to ?? '');

  function commitCustom(nextFrom: string, nextTo: string) {
    setFrom(nextFrom);
    setTo(nextTo);
    // Only commit a complete, ordered range — otherwise fall back to the pane defaults so a
    // half-typed range never triggers a 400.
    const valid = nextFrom !== '' && nextTo !== '' && nextFrom <= nextTo;
    setQuery({ ...query, period: valid ? { from: nextFrom, to: nextTo } : undefined });
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label htmlFor={selectId} className="flex items-center gap-1.5 text-sm text-brand-dark/55">
        <CalendarRange size={15} className="text-brand-primary/50" />
        Periode
      </label>
      <select
        id={selectId}
        aria-label="Periode"
        value={mode}
        onChange={(e) => {
          const next = e.target.value as Mode;
          setMode(next);
          if (next === 'default') setQuery({ ...query, period: undefined });
          else if (next === 'custom') commitCustom(from, to);
          else setQuery({ ...query, period: next });
        }}
        className={SELECT_CLASS}
      >
        <option value="default">Standard</option>
        {PERIODS.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
        <option value="custom">Tilpasset…</option>
      </select>
      {mode === 'custom' && (
        <span className="flex items-center gap-1.5">
          <input
            type="date"
            aria-label="Fra dato"
            value={from}
            max={to || undefined}
            onChange={(e) => commitCustom(e.target.value, to)}
            className={SELECT_CLASS}
          />
          <span className="text-brand-dark/40">–</span>
          <input
            type="date"
            aria-label="Til dato"
            value={to}
            min={from || undefined}
            onChange={(e) => commitCustom(from, e.target.value)}
            className={SELECT_CLASS}
          />
        </span>
      )}
    </div>
  );
}
